import React, { useState, useEffect, useCallback } from 'react';

import { Play, Users, Clock, RotateCcw } from 'lucide-react';

const ReligionGame = () => {
  const [gameState, setGameState] = useState('setup');
  const [gameWinner, setGameWinner] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [positions, setPositions] = useState([]);
  const [scores, setScores] = useState([]);
  const [lapsCompleted, setLapsCompleted] = useState([]);
  const [diceValue, setDiceValue] = useState(1);
  const [isRolling, setIsRolling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [gameMode, setGameMode] = useState('rounds1');
  const [timeLeft, setTimeLeft] = useState(null);
  const [animatingPlayer, setAnimatingPlayer] = useState(-1);
  const [gameMessage, setGameMessage] = useState('Velkommen til spillet!');
  const [waitingForAnswer, setWaitingForAnswer] = useState(false);
  const [hoveredField, setHoveredField] = useState(null);
  const [selectedPlayersForReflection, setSelectedPlayersForReflection] = useState(new Set());
  const [playerConfigs, setPlayerConfigs] = useState([
    { name: '', color: 0, avatar: 0 },
    { name: '', color: 1, avatar: 1 },
    { name: '', color: 2, avatar: 2 }
  ]);
  const [customTime, setCustomTime] = useState(15);
  const [timerIntervalId, setTimerIntervalId] = useState(null);

  const PLAYER_COLORS = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
  const PLAYER_AVATARS = ['👤', '⭐', '💡', '🕊️', '🌍', '🤝', '🧠', '📖', '🌟', '🔑', '✨', '🧭', '🔷', '🔶', '🟡', '⚫'];
  const AVATAR_NAMES = ['Person', 'Stjerne', 'Lyspære', 'Due', 'Verden', 'Håndtrykk', 'Hjerne', 'Bok', 'Stjerne2', 'Nøkkel', 'Gnister', 'Kompass', 'Diamant', 'Firkant', 'Sirkel', 'Sirkel'];
  const BOARD_SIZE = 20;

  const FACT_QUESTIONS = [
    { religion: "Kristendom", question: "Hva er kristendommens hellige bok?", options: ["Koranen", "Toraen", "Vedaene", "Bibelen"], correct: 3 },
    { religion: "Kristendom", question: "Hvilken høytid feirer kristne Jesu fødsel?", options: ["Jul", "Påske", "Pinse", "Kristi himmelfartsdag"], correct: 0 },
    { religion: "Kristendom", question: "Hvor mange disipler (apostler) hadde Jesus?", options: ["10", "11", "13", "12"], correct: 3 },
    { religion: "Kristendom", question: "Hva kalles lederen for den katolske kirke, som holder til i Vatikanet?", options: ["Paven", "Patriarken", "Biskopen", "Kardinalen"], correct: 0 },
    { religion: "Kristendom", question: "Hvilken dag i uken regnes som den viktigste for kristne?", options: ["Fredag", "Søndag", "Lørdag", "Mandag"], correct: 1 },
    { religion: "Kristendom", question: "Hva kalles den viktigste kristne bekjennelsen?", options: ["Fadervår", "Gloria", "Trosbekjennelsen", "Ave Maria"], correct: 2 },
    { religion: "Kristendom", question: "Hvilken høytid feirer Jesus' oppstandelse?", options: ["Jul", "Pinse", "Påske", "Maria budskapsdag"], correct: 2 },
    { religion: "Kristendom", question: "Hva kalles den rituelle handlingen der en person blir en del av kirken?", options: ["Dåp", "Nattverd", "Konfirmasjon", "Vigsel"], correct: 0 },
    { religion: "Kristendom", question: "Hvilken profet forkynte Jesus' komme?", options: ["Moses", "Jesaja", "Johannes Døperen", "Elias"], correct: 2 },
    { religion: "Kristendom", question: "Hva består Bibelen av?", options: ["Kun Det nye testamente", "Apokryfene", "Det gamle og Det nye testamente", "Kun Det gamle testamente"], correct: 2 },
    { religion: "Kristendom", question: "Hvor ble Jesus født?", options: ["Nazaret", "Jerusalem", "Betlehem", "Kapernaum"], correct: 2 },
    { religion: "Kristendom", question: "Hva er navnet på den talen hvor Jesus snakker om nestekjærlighet og etikk?", options: ["Bergprekenen", "De ti bud", "Fadervår", "Det dobbelte kjærlighetsbudet"], correct: 0 },
    { religion: "Kristendom", question: "Hvilken dag markeres Jesus' død på korset?", options: ["Skjærtorsdag", "Langfredag", "Palmesøndag", "Påskedag"], correct: 1 },
    { religion: "Kristendom", question: "Hva symboliserer korset for kristne?", options: ["Døden", "Kirken", "Jesu korsfestelse og seier over døden", "Treenigheten"], correct: 2 },
    { religion: "Kristendom", question: "Hva kalles de første fire bøkene i Det nye testamente?", options: ["Paulusbrevene", "Apostlenes gjerninger", "Johannes' åpenbaring", "Evangeliene"], correct: 3 },
    { religion: "Kristendom", question: "Hva symboliserer brød og vin under nattverden?", options: ["Jesu legeme og blod", "Offergaver til Gud", "Fellesmåltid", "Vingårdene i Israel"], correct: 0 },
    { religion: "Kristendom", question: "Hva er den viktigste bønnen i kristendommen?", options: ["Fadervår", "Ave Maria", "Ære være Faderen", "Den apostoliske trosbekjennelsen"], correct: 0 },
    { religion: "Kristendom", question: "Hva er navnet på de tre personene i den kristne treenigheten?", options: ["Peter, Paulus, Johannes", "Abraham, Isak, Jakob", "Paven, Kardinalen, Biskopen", "Faderen, Sønnen, Den hellige ånd"], correct: 3 },
    { religion: "Islam", question: "Hva er islams hellige bok?", options: ["Talmud", "Koranen", "Bibelen", "Tripitaka"], correct: 1 },
    { religion: "Islam", question: "Hvor mange daglige bønner er foreskrevet i islam?", options: ["3 bønner", "6 bønner", "4 bønner", "5 bønner"], correct: 3 },
    { religion: "Islam", question: "Hva kalles pilegrimsreisen til Mekka?", options: ["Hadsj", "Umrah", "Jihad", "Salah"], correct: 0 },
    { religion: "Islam", question: "Hva er navnet på islams viktigste profet?", options: ["Jesus", "Abraham", "Muhammad", "Moses"], correct: 2 },
    { religion: "Islam", question: "Hva kalles muslimenes fastemåned?", options: ["Id", "Ramadan", "Hadsj", "Zakat"], correct: 1 },
    { religion: "Islam", question: "Hva heter moskeens tårn, derfra bønneropet ropes ut?", options: ["Mihrab", "Minbar", "Minaret", "Qibla"], correct: 2 },
    { religion: "Islam", question: "Hvilken retning ber muslimer mot?", options: ["Jerusalem", "Medina", "Mekka", "Damaskus"], correct: 2 },
    { religion: "Islam", question: "Hva kalles de fem grunnleggende pliktene i islam?", options: ["De fem søylene", "De fem bud", "De hellige skriftene", "De fem sannheter"], correct: 0 },
    { religion: "Islam", question: "Hva kalles islams trosbekjennelse?", options: ["Shahada", "Salah", "Zakat", "Sawm"], correct: 0 },
    { religion: "Islam", question: "Hva er den mest vanlige oversettelsen av ordet 'Islam'?", options: ["Fred", "Barmhjertighet", "Underkastelse", "Tro"], correct: 2 },
    { religion: "Islam", question: "Hvilken engel brakte budskapet fra Gud til profeten Muhammad?", options: ["Mikael", "Gabriel", "Rafael", "Uriel"], correct: 1 },
    { religion: "Islam", question: "Hvilket begrep beskriver muslimenes innsats for å bli et bedre menneske?", options: ["Hadsj", "Iftar", "Jihad", "Suhur"], correct: 2 },
    { religion: "Islam", question: "Hvilket språk er Koranen originalt skrevet på?", options: ["Hebraisk", "Persisk", "Arabisk", "Aramaisk"], correct: 2 },
    { religion: "Islam", question: "Hva er navnet på den religiøse lederen som leder bønnen i moskeen?", options: ["Paven", "Biskopen", "Rabbineren", "Imamen"], correct: 3 },
    { religion: "Islam", question: "Hvilken viktig by ble profeten Muhammad født i?", options: ["Medina", "Jerusalem", "Kairo", "Mekka"], correct: 3 },
    { religion: "Islam", question: "Hva er den viktigste høytiden i islam, som avslutter fasten?", options: ["Id al-fitr", "Ramadan", "Hadsj", "Id al-adha"], correct: 0 },
    { religion: "Islam", question: "Hva kalles mat som er tillatt ifølge islamske regler?", options: ["Kosher", "Vegan", "Halal", "Harām"], correct: 2 },
    { religion: "Islam", question: "Hva er et islamsk gudshus?", options: ["Kirke", "Synagoge", "Tempel", "Moské"], correct: 3 },
    { religion: "Jødedom", question: "Hva er jødedommens helligste skrift?", options: ["Mishna", "Talmud", "Tanakh", "Toraen"], correct: 3 },
    { religion: "Jødedom", question: "Hvilken dag i uken er jødenes hellige hviledag, sabbaten?", options: ["Lørdag", "Søndag", "Fredag", "Mandag"], correct: 0 },
    { religion: "Jødedom", question: "Hva kalles seremonien som markerer at en jødisk gutt blir religiøst myndig?", options: ["Bar Mitzva", "Bat Mitzva", "Brit Milah", "Kiddush"], correct: 0 },
    { religion: "Jødedom", question: "Hva er navnet på den niarmede lysestaken som brukes under hanukka?", options: ["Menora", "Hanukka-menora", "Sabbatlys", "Mezuza"], correct: 1 },
    { religion: "Jødedom", question: "Hvilken høytid feirer jødenes utgang fra Egypt?", options: ["Yom Kippur", "Rosj Hasjana", "Sukkot", "Pesach"], correct: 3 },
    { religion: "Jødedom", question: "Hva kalles jødenes gudshus?", options: ["Synagoge", "Kirke", "Moské", "Tempel"], correct: 0 },
    { religion: "Jødedom", question: "Hvilken profet mottok Toraen og De ti bud fra Gud?", options: ["Abraham", "David", "Jakob", "Moses"], correct: 3 },
    { religion: "Jødedom", question: "Hva kalles jødenes nyttårshøytid?", options: ["Pesach", "Yom Kippur", "Sukkot", "Rosj Hasjana"], correct: 3 },
    { religion: "Jødedom", question: "Hva er navnet på den syvarmede lysestaken som symboliserer skapelsens dager?", options: ["Sjofar", "Menora", "Hanukka-menora", "Mezuza"], correct: 1 },
    { religion: "Jødedom", question: "Hvilken høytid kalles 'forsoningsdagen'?", options: ["Rosj Hasjana", "Yom Kippur", "Pesach", "Sukkot"], correct: 1 },
    { religion: "Jødedom", question: "Hva betyr 'kosher'?", options: ["Bønnehus", "Hellig skrift", "Regler for tillatt mat", "Religiøs leder"], correct: 2 },
    { religion: "Jødedom", question: "Hva kalles hodeplagget som ortodokse jødiske menn bruker?", options: ["Kippa", "Tallit", "Tefillin", "Mezuza"], correct: 0 },
    { religion: "Jødedom", question: "Hvilket instrument brukes under Rosj Hasjana for å varsle omvendelse?", options: ["Harpe", "Sjofar", "Fløyte", "Tromme"], correct: 1 },
    { religion: "Jødedom", question: "Hva er Talmud?", options: ["En profet", "Et tempel", "En samling av muntlige tradisjoner og tolkninger", "En høytid"], correct: 2 },
    { religion: "Jødedom", question: "Hvor mange bøker består Toraen av?", options: ["Tre", "Fire", "Seks", "Fem"], correct: 3 },
    { religion: "Jødedom", question: "Hva er symbolet for jødedommen?", options: ["Korset", "Davidsstjernen", "Halvmånen og stjernen", "Dharmahjulet"], correct: 1 },
    { religion: "Jødedom", question: "Hva er navnet på seremonien for en jødisk jente som blir religiøst myndig?", options: ["Brit Milah", "Bat Mitzva", "Bar Mitzva", "Kiddush"], correct: 1 },
    { religion: "Jødedom", question: "Hvilket klesplagg bruker noen jødiske menn under bønn?", options: ["Turban", "Kippa", "Tallit", "Shabbat"], correct: 2 },
    { religion: "Buddhisme", question: "Hvem grunnla buddhismen?", options: ["Jesus", "Muhammad", "Guru Nanak", "Siddhartha Gautama (Buddha)"], correct: 3 },
    { religion: "Buddhisme", question: "Hva kalles buddhismens mål om å unnslippe gjenfødelsessyklusen?", options: ["Karma", "Dharma", "Samsara", "Nirvana"], correct: 3 },
    { religion: "Buddhisme", question: "Hva kalles de fire grunnleggende prinsippene i buddhismen?", options: ["De fire edle sannheter", "Den åttedelte veien", "De fem forskrifter", "De tre juvelene"], correct: 0 },
    { religion: "Buddhisme", question: "Hvilken sittestilling brukes ofte i buddhistisk meditasjon?", options: ["Knelende", "Stående", "Lotus-posisjon", "Liggende"], correct: 2 },
    { religion: "Buddhisme", question: "Hva kalles veien som fører til opphør av lidelse?", options: ["Den åttedelte veien", "Nirvana", "Samsara", "Karma"], correct: 0 },
    { religion: "Buddhisme", question: "Hva er karma i buddhismen?", options: ["Loven om årsak og virkning", "En gud", "Et tempel", "En meditasjon"], correct: 0 },
    { religion: "Buddhisme", question: "Hva kalles gjenfødelsessyklusen?", options: ["Karma", "Dharma", "Nirvana", "Samsara"], correct: 3 },
    { religion: "Buddhisme", question: "Hva kalles de tre viktige elementene i buddhismen?", options: ["Buddha, Dharma, Sangha", "Vishnu, Shiva, Brahma", "Fortid, nåtid, fremtid", "Kropp, sjel, ånd"], correct: 0 },
    { religion: "Buddhisme", question: "Hva er den første edle sannhet?", options: ["Lidelse har en årsak", "Det finnes en vei ut av lidelse", "Livet er lidelse", "Lidelse kan opphøre"], correct: 2 },
    { religion: "Buddhisme", question: "Hva kalles buddhistiske munker?", options: ["Brahmin", "Lama", "Guru", "Bhikkhu"], correct: 3 },
    { religion: "Buddhisme", question: "Hvilket tre satt Buddha under da han ble opplyst?", options: ["Bodhi-treet", "Eiketre", "Oliventre", "Sedertre"], correct: 0 },
    { religion: "Buddhisme", question: "Hva er det vanlige navnet på et buddhistisk tempel?", options: ["Kirke", "Pagode", "Moské", "Synagoge"], correct: 1 },
    { religion: "Buddhisme", question: "Hva betyr 'Buddha'?", options: ["Den hellige", "Den store lederen", "Den visdomsfulle", "Den opplyste"], correct: 3 },
    { religion: "Buddhisme", question: "Hva er Tripitaka?", options: ["En meditasjonsteknikk", "En type tempel", "En samling av hellige tekster", "En buddhistisk gud"], correct: 2 },
    { religion: "Buddhisme", question: "Hvilken av de tre juvelene betyr 'det buddhistiske samfunnet'?", options: ["Buddha", "Sangha", "Dharma", "Karma"], correct: 1 },
    { religion: "Hinduisme", question: "Hva kalles hinduismens eldste hellige skrifter?", options: ["Bibelen", "Koranen", "Upanisjadene", "Vedaene"], correct: 3 },
    { religion: "Hinduisme", question: "Hva er karma i hinduismen?", options: ["En gud", "Loven om årsak og virkning", "Et tempel", "En bønn"], correct: 1 },
    { religion: "Hinduisme", question: "Hva kalles troen på gjenfødelse?", options: ["Moksha", "Dharma", "Reinkarnasjon", "Yoga"], correct: 2 },
    { religion: "Hinduisme", question: "Hvilken gud regnes som ødelegger og gjenskaper?", options: ["Brahma", "Vishnu", "Shiva", "Ganesha"], correct: 2 },
    { religion: "Hinduisme", question: "Hvilket dyr regnes som hellig?", options: ["Elefant", "Ku", "Tiger", "Ape"], correct: 1 },
    { religion: "Hinduisme", question: "Hva er navnet på hinduismens sosiale system med rangordning?", options: ["Varna", "Dharma", "Moksha", "Kastesystemet"], correct: 3 },
    { religion: "Hinduisme", question: "Hvilken gud avbildes med elefanthode?", options: ["Shiva", "Vishnu", "Brahma", "Ganesha"], correct: 3 },
    { religion: "Hinduisme", question: "Hva er det endelige målet for livet i hinduismen?", options: ["Moksha (frigjøring fra gjenfødelse)", "Rikdom", "Makt", "Berømmelse"], correct: 0 },
    { religion: "Hinduisme", question: "Hvilken elv regnes som den helligste?", options: ["Nilen", "Amazonas", "Ganges", "Yangtze"], correct: 2 },
    { religion: "Hinduisme", question: "Hva kalles den hellige stavelsen som brukes i meditasjon og ved resitasjon?", options: ["Amen", "Halleluja", "Namaste", "Om"], correct: 3 },
    { religion: "Hinduisme", question: "Hvilken kjent praksis for kropp og sinn har sitt opphav i hinduismen?", options: ["Meditasjon", "Pilgrimsreise", "Yoga", "Fasten"], correct: 2 },
    { religion: "Hinduisme", question: "Hva kalles pliktene og de etiske reglene som gjelder for en hindu?", options: ["Karma", "Moksha", "Samsara", "Dharma"], correct: 3 },
    { religion: "Hinduisme", question: "Hva er navnet på guden som er skaperen av universet?", options: ["Vishnu", "Shiva", "Krishna", "Brahma"], correct: 3 },
    { religion: "Hinduisme", question: "Hva er en puja?", options: ["En pilegrimsreise", "En type yoga", "En hellig skrift", "En rituell handling og tilbedelse"], correct: 3 },
    { religion: "Hinduisme", question: "Hvilken høytid feires med lys, farger og fyrverkeri for å feire det godes seier over det onde?", options: ["Holi", "Diwali", "Navaratri", "Maha Shivaratri"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hva bygger livssynshumanister sine verdier på?", options: ["Fornuft og menneskerettigheter", "Religiøse skrifter", "Tradisjon", "Åpenbaring"], correct: 0 },
    { religion: "Livssynshumanisme", question: "Hva er livssynshumanisters syn på guder?", options: ["De tror på alle guder", "De tror på én gud", "De tror ikke på guder", "De er usikre på om guder finnes"], correct: 2 },
    { religion: "Livssynshumanisme", question: "Hva er viktigst når humanister tar etiske valg?", options: ["Religiøse bud", "Konsekvenser for menneskelig velferd", "Tradisjon", "Autoritet"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hva kalles seremonien Human-Etisk Forbund tilbyr unge i tiendeklasse?", options: ["Dåp", "Navnefest", "Borgerlig konfirmasjon", "Vigsel"], correct: 2 },
    { religion: "Livssynshumanisme", question: "Hva mener humanister om vitenskapen?", options: ["Den er uviktig", "En av flere likeverdige metoder", "Den beste måten å forstå verden på", "Den er farlig"], correct: 2 },
    { religion: "Livssynshumanisme", question: "Hva kjennetegner humanistisk moral?", options: ["Basert på medmenneskelighet", "Fokuserer på tradisjon", "Bygger på religiøse bud", "Kommer fra høyere makter"], correct: 0 },
    { religion: "Livssynshumanisme", question: "Hvilket symbol bruker Human-Etisk Forbund?", options: ["Kors", "Happy Human", "Halvmåne", "Om"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hva er humanisters syn på døden?", options: ["Det finnes et liv etter døden", "Døden er den endelige slutten på eksistensen", "Reinkarnasjon", "Det varierer veldig"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hvilke verdier er sentrale i livssynshumanismen?", options: ["Toleranse og menneskerettigheter", "Tradisjon og autoritet", "Lydighet og underdanighet", "Mystikk og åpenbaring"], correct: 0 },
    { religion: "Livssynshumanisme", question: "Hva er humanisters forhold til andre livssyn?", options: ["De avviser alle religioner", "De respekterer andres rett til tro", "De angriper religiøse", "De er likegyldige"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hvilken filosofisk retning har livssynshumanismen sitt utgangspunkt i?", options: ["Materialisme", "Idealisme", "Empirisme", "Rasjonalisme"], correct: 3 },
    { religion: "Livssynshumanisme", question: "Hva er en humanistisk tilnærming til etikk?", options: ["Pliktetikk", "Utilitarisme", "Dydsetikk", "Konsekvensetikk"], correct: 3 },
    { religion: "Livssynshumanisme", question: "Hva betyr 'autonomi' i en humanistisk sammenheng?", options: ["Underkastelse under en høyere makt", "Selvbestemmelse og frihet", "Lydighet mot tradisjon", "Felleskapets beste"], correct: 1 },
    { religion: "Livssynshumanisme", question: "Hva er Human-Etisk Forbunds syn på ekteskap?", options: ["De er imot ekteskap", "De anser ekteskap som irrelevant", "De tilbyr et humanistisk vigselsalternativ", "De tilbyr kun religiøse ekteskap"], correct: 2 },
    { religion: "Livssynshumanisme", question: "Hva kalles livssynshumanisters syn på at det ikke finnes guder?", options: ["Agnostisisme", "Ateisme", "Teisme", "Panteisme"], correct: 1 }
  ];

  const REFLECTION_QUESTIONS = [
    "Hvorfor har mennesker så mange ulike religiøse tradisjoner?",
    "Hvilke verdier og prinsipper er felles for mange religioner og livssyn?",
    "Hvordan kan mennesker med ulik tro leve sammen i fred?",
    "Hva kan vi lære av å studere ulike religioner og livssyn?",
    "Hvordan påvirker religion og livssyn hvordan vi ser på verden?",
    "Hvilken rolle spiller ritualer og seremonier for mennesker med ulike livssyn?",
    "Hvordan har religioner påvirket kunst, musikk og kultur?",
    "Hva betyr religionsfrihet og ytringsfrihet for samfunnet vårt?",
    "Hvordan kan dialog mellom ulike trosretninger føre til bedre forståelse?",
    "Hvilke utfordringer kan oppstå når ulike religioner og kulturer møtes?",
    "Hvordan kan vi vise respekt for andres tro, selv om vi ikke er enige?",
    "Hva er forskjellen mellom å være religiøs og å være åndelig?",
    "Hvordan påvirker religion og livssyn synet på miljø og natur?",
    "Hvilken rolle spiller fellesskap i ulike religioner?",
    "Hvordan kan vi bekjempe fordommer mot ulike trosretninger?",
    "Hvilke likheter finner du mellom de hellige fortellingene i ulike religioner?",
    "Hvorfor er det viktig å lære om andres tro og livssyn?",
    "Hva kan religioner lære av hverandre?",
    "Hvordan kan unge mennesker finne sin egen vei i et samfunn med mange ulike livssyn?",
    "Hvilken rolle spiller religion når det gjelder å gi mening og håp til mennesker?",
    "Hvordan forholder ulike religioner seg til vitenskapelige oppdagelser?",
    "Hvilke etiske dilemmaer oppstår når religion møter det moderne samfunnet?",
    "Hvordan påvirker globalisering måten religioner praktiseres på?",
    "Hva er forskjellen på fundamentalisme og moderate tolkninger av religion?",
    "Hvordan kan religioner bidra til fred og forsoning i verden?",
    "Hvordan er synet på kvinner i ulike religiøse tradisjoner?",
    "Hva er viktigheten av hellige steder for troende?",
    "Hvilke utfordringer møter religioner i det moderne samfunn?",
    "Hvordan kan vi skille mellom kultur og religion?",
    "Hva er betydningen av profeter og åndelige ledere i ulike religioner?",
    "Hvordan forholder ulike tradisjoner seg til lidelse og død?",
    "Hvilken rolle spiller bønn og meditasjon i ulike religioner?",
    "Hvordan påvirker religiøs tilhørighet vår identitet?",
    "Hva er forskjellen mellom toleranse og aksept?",
    "Hvordan kan vi forstå radikalisering innenfor religioner?",
    "Hvilken betydning har hellige tekster i dag?",
    "Hvordan forholder religioner seg til menneskerettigheter?",
    "Hva er religionens rolle i en verden som er dominert av vitenskap?",
    "Hvordan kan dialog mellom religioner styrke et samfunn?",
    "Hvilke positive bidrag gir religioner til samfunnet?",
    "Hvordan håndterer ulike religioner konflikter og tilgivelse?",
    "Hva betyr det å være en god nabo på tvers av trosgrenser?",
    "Hvordan kan vi lære om religioner uten å skape stereotyper?",
    "Hvilken rolle spiller pilgrimsreiser og hellige reiser?",
    "Hvordan forholder ulike religioner seg til materielle goder og rikdom?",
    "Hva kan vi lære av religiøse høytider og feiringer?",
    "Hvordan påvirker religiøs oppvekst barns syn på verden?",
    "Hvilken betydning har symboler og ritualer for troende?",
    "Hvordan kan et livssyn uten en tro på en gud gi mening og etikk?",
    "Hvorfor er historien til de store religionene så viktig å kjenne til?",
    "Hvordan kan religion både være en kilde til konflikt og til fred?",
    "Hva er ditt personlige syn på religionenes rolle i dagens samfunn?",
    "Hva betyr det å ha en 'livssynsåpen' holdning?",
    "Hvordan kan vi skille mellom en persons handlinger og den religionen de tilhører?",
    "Hvilke etiske dilemmaer kan oppstå når religion og vitenskap møtes?",
    "Hvordan kan personlig tro uttrykkes i et moderne samfunn?",
    "Hva er likheter og ulikheter mellom livssynshumanisme og religiøse livssyn?",
    "Hvorfor er det viktig å lære om forskjellige livssyn, selv om man ikke er troende selv?"
  ];

  const CHALLENGE_CARDS = [  
    {
        "text": "Tegn og forklar hovedsymbolet for kristendommen.",
        "answer": "✝️ Et kors. Det symboliserer Jesu korsfestelse og oppstandelse."
    },
    {
        "text": "Tegn Davidsstjernen og forklar hva den symboliserer for jøder.",
        "answer": "✡️ En stjerne med seks spisser, dannet av to overlappende trekanter. Symbolet står for Guds pakt med David og det jødiske folk."
    },
    {
        "text": "Tegn det vanligste symbolet for islam og forklar betydningen.",
        "answer": "☪️ En halvmåne og en stjerne. Halvmånen representerer fremskritt, og stjernen representerer Guds lys og kunnskap."
    },
    {
        "text": "Tegn dharmahjulet og forklar hvorfor det har åtte eiker.",
        "answer": "☸️ Et hjul med åtte eiker. De åtte eikene representerer 'Den åttedelte veien' – en vei til opplysning og frigjøring fra lidelse."
    },
    {
        "text": "Tegn Om-symbolet og si hvordan det uttales.",
        "answer": "🕉️ Et stilisert symbol som representerer den hellige lyden 'Om' eller 'Aum'. Det er den helligste stavelsen i hinduismen."
    },
    {
        "text": "Demonstrer lystenning under hanukka og forklar hvorfor man gjør det.",
        "answer": "🕯️ Man tenner et lys hver av de åtte dagene i hanukka, som feirer mirakelet i tempelet i Jerusalem, der olje som skulle vare én dag, varte i åtte."
    },
    {
        "text": "Mime en scene fra julefortellingen.",
        "answer": "🎄 Kan inneholde mimer av Maria, Josef, Jesu fødsel i en stall, vise frem en stjerne, eller presentere gaver fra de tre vise menn."
    },
    {
        "text": "Forklar hva som skiller en fundamentalistisk tolkning av religion fra en moderat tolkning.",
        "answer": "🔎 En fundamentalistisk tolkning er ofte streng og bokstavelig, mens en moderat tolkning er mer åpen for endring, modernitet og nye fortolkninger."
    },
    {
        "text": "Forklar med egne ord hvorfor en livssynshumanist ikke ønsker å døpe sitt barn.",
        "answer": "👶 Livssynshumanister tror ikke på Gud og baserer sine verdier på fornuft og menneskerettigheter. De tilbyr egne seremonier, som navnefest, som alternativ til dåp."
    },
    {
        "text": "Forklar hva reinkarnasjon er. Hvilke religioner tror på dette, og hvorfor?",
        "answer": "🔄 Reinkarnasjon er troen på at sjelen eller bevisstheten gjenfødes i en ny kropp etter døden. Dette er en sentral tanke i hinduismen og buddhismen. Det gir en mulighet for å oppnå åndelig utvikling over flere liv."
    },
    {
        "text": "Forklar forskjellen på en moské og en kirke.",
        "answer": "⛪ En moské er islams hellige forsamlingshus, hvor muslimer ber og tilber Allah. En kirke er et hellig bygg for kristne, hvor de ber og tilber Gud."
    },
    {
        "text": "Fortell en kort historie fra Bibelen eller Koranen.",
        "answer": "📖 Eksempel: Noahs Ark, historien om Jesus fødsel, eller historien om profeten Abraham."
    },
    {
        "text": "Lag et slagord som fremmer religionsfrihet og toleranse.",
        "answer": "🕊️ Eksempel: 'Din tro, din vei, vår respekt'."
    },
    {
        "text": "Gjenfortell en hinduistisk myte om en av de store gudene.",
        "answer": "✨ Eksempel: Fortellingen om guden Shiva som ødelegger og gjenskaper, eller historien om guden Ganesha."
    },
    {
        "text": "Forklar med dine egne ord hva de 'fem søylene' i islam er.",
        "answer": "🕌 De fem grunnleggende pliktene for alle muslimer: trosbekjennelsen, bønnen, velferdsbidraget, fasten og pilegrimsreisen til Mekka."
    },
    {
        "text": "Forklar hva som skjer under en borgerlig konfirmasjon.",
        "answer": "🎓 En seremoni som feirer overgangen fra barn til voksen, basert på humanistiske verdier. Deltakerne får kunnskap om etikk, filosofi og livssyn, uten religiøse ritualer."
    },
    {
        "text": "Tegn symbolet for livssynshumanisme, og forklar hvorfor det passer godt til livssynet.",
        "answer": "🫂 En stilisert menneskefigur som holder rundt en jordklode. Det symboliserer menneskets sentrale rolle, ansvar for verden og det globale fellesskapet."
    },
    {
        "text": "Tegn en yin og yang-symbol og forklar hva det representerer.",
        "answer": "☯️ En sirkel delt i to halvdeler, en svart og en hvit, med en liten sirkel av motsatt farge i hver. Symboliserer balansen mellom motstridende krefter, som lys og mørke, som er nødvendig for harmoni."
    },
    {
        "text": "Gjør en yoga-posisjon og forklar hvorfor yoga er en viktig praksis for mange hinduer.",
        "answer": "🧘 Yoga er en fysisk, mental og åndelig praksis som hjelper utøvere med å forene kropp og sinn for å oppnå åndelig innsikt eller forening med det guddommelige."
    },
    {
        "text": "Fortell hva 'sabbaten' betyr for jøder og kristne, og når den feires av de to religionene.",
        "answer": "🗓️ Sabbaten er en hellig hviledag. Jøder feirer den fra fredag kveld til lørdag kveld. De kristne har tradisjonelt flyttet feiringen til søndag, som er dagen for Jesu oppstandelse."
    },
    {
        "text": "Forklar hvorfor buddhister unngår å skade levende vesener.",
        "answer": "🚫 Denne praksisen, kjent som ahimsa, er sentral. Buddhister tror at alle levende vesener har potensial til å oppnå opplysning, og at man bør unngå å forårsake lidelse."
    }
  ];

  const CHANCE_CARDS = [
    { text: "Du hjelper en klassekamerat. Bra gjort!", points: 2 },
    { text: "Du viser respekt for andres tro!", points: 1 },
    { text: "Du har lært noe nytt og får 3 bonuspoeng!", points: 3 },
    { text: "Du er en god lytter og får 1 poeng.", points: 1 },
    { text: "Du har vist at du er tolerant og åpen. Få 2 poeng!", points: 2 },
    { text: "Du har vært en god lagspiller og får 2 poeng.", points: 2 },
    { text: "Du blandet sammen en profet med en filosof. Det kostet deg dyrt.", points: -2 },
    { text: "Du prøvde deg på en smart gjetning og traff blink. Ren flaks!", points: 1 },
    { text: "Du ble fristet av et feilaktig svaralternativ. Det skjer den beste.", points: -1 },
    { text: "Du bidro med en morsom vits. Ingen lo, men du får 0 poeng for innsatsen.", points: 0 },
    { text: "Du forvekslet en hellig by med et land. Det gir ingen poeng.", points: 0 },
    { text: "Du ble så engasjert i debatten at du glemte å svare. Neste gang!", points: -1 },
    { text: "Du har så god karma i dag at du får ekstrapoeng.", points: 2 },
    { text: "Du gjettet riktig på et umulig spørsmål. Legg til 2 poeng!", points: 2 },
    { text: "Du forvekslet et ritual med en høytid. Det trekkes 1 poeng for feilen.", points: -1 },
    { text: "Du leste deg opp på et ukjent livssyn. Få 2 poeng for engasjementet!", points: 2 },
    { text: "Du svarte så innviklet at ingen forstod deg. Resultat: 0 poeng.", points: 0 },
    { text: "Du prøvde å overbevise en medspiller om ditt svar. Du mister 1 poeng for usportslig oppførsel.", points: -1 },
    { text: "Du kom på en ny vri på et av utfordringskortene. Få 1 poeng for kreativitet!", points: 1 }
  ];

  useEffect(() => {
    return () => {
      if (timerIntervalId) {
        clearInterval(timerIntervalId);
      }
    };
  }, [timerIntervalId]);

  const endGame = useCallback(() => {
    if (gameState === 'finished') return;

    if (timerIntervalId) {
      clearInterval(timerIntervalId);
      setTimerIntervalId(null);
    }

    const finalScores = players.map((player, index) => ({
      player: player,
      score: scores[index],
      rounds: lapsCompleted[index]
    }));
    
    const maxScore = Math.max(...finalScores.map(p => p.score));
    
    const winners = finalScores.filter(playerData => playerData.score === maxScore).map(p => p.player);
    
    setGameWinner({
      winners: winners,
      finalScores: finalScores
    });
    setGameState('finished');
  }, [players, scores, lapsCompleted, gameState, timerIntervalId]);

  useEffect(() => {
    if (gameMode === 'time' && timeLeft !== null && timeLeft <= 0 && gameState === 'playing') {
      endGame();
    }
  }, [timeLeft, gameMode, gameState, endGame]);

  const checkGameEnd = () => {
    const maxRounds = gameMode === 'rounds1' ? 1 : gameMode === 'rounds2' ? 2 : null;
    if (maxRounds && players.length > 0 && lapsCompleted.every(laps => laps >= maxRounds)) {
      endGame();
      return true;
    }
    return false;
  };

  const getBoardPosition = (index) => {
    const cellSize = 120;
    const positions = [
      { x: -cellSize * 3, y: -cellSize * 2 },
      { x: -cellSize * 2, y: -cellSize * 2 },
      { x: -cellSize, y: -cellSize * 2 },
      { x: 0, y: -cellSize * 2 },
      { x: cellSize, y: -cellSize * 2 },
      { x: cellSize * 2, y: -cellSize * 2 },
      { x: cellSize * 3, y: -cellSize * 2 },
      { x: cellSize * 3, y: -cellSize },
      { x: cellSize * 3, y: 0 },
      { x: cellSize * 3, y: cellSize },
      { x: cellSize * 3, y: cellSize * 2 },
      { x: cellSize * 2, y: cellSize * 2 },
      { x: cellSize, y: cellSize * 2 },
      { x: 0, y: cellSize * 2 },
      { x: -cellSize, y: cellSize * 2 },
      { x: -cellSize * 2, y: cellSize * 2 },
      { x: -cellSize * 3, y: cellSize * 2 },
      { x: -cellSize * 3, y: cellSize },
      { x: -cellSize * 3, y: 0 },
      { x: -cellSize * 3, y: -cellSize }
    ];
    return positions[index] || { x: 0, y: 0 };
  };

  const getFieldType = (index) => {
    if (index === 0) return 'start';
    const types = ['fact', 'reflection', 'fact', 'challenge', 'fact', 'chance'];
    return types[(index - 1) % types.length];
  };

  const getFieldInfo = (type) => {
    const info = {
      start: { color: '#dc2626', name: 'START', icon: '🏁' },
      fact: { color: '#2563eb', name: 'FAKTA', icon: '📚' },
      reflection: { color: '#16a34a', name: 'REFLEKSJON', icon: '💭' },
      challenge: { color: '#ea580c', name: 'UTFORDRING', icon: '⚡' },
      chance: { color: '#eab308', name: 'SJANSE', icon: '🎲' }
    };
    return info[type] || info.fact;
  };

  const rollDice = () => {
    if (isRolling || waitingForAnswer) return;

    const maxRounds = gameMode === 'rounds1' ? 1 : gameMode === 'rounds2' ? 2 : null;
    if (maxRounds && lapsCompleted[currentPlayerIndex] >= maxRounds) {
      setGameMessage(`${players[currentPlayerIndex].name} er allerede i mål. Vent på de andre!`);
      nextTurn();
      return;
    }
    
    setIsRolling(true);
    setGameMessage('Terningen ruller...');
    
    let rollCount = 0;
    const rollInterval = setInterval(() => {
      setDiceValue(Math.floor(Math.random() * 6) + 1);
      rollCount++;
      
      if (rollCount > 15) {
        clearInterval(rollInterval);
        const finalValue = Math.floor(Math.random() * 6) + 1;
        setDiceValue(finalValue);
        setIsRolling(false);
        setGameMessage(`${players[currentPlayerIndex].name} kastet ${finalValue}!`);
        movePlayer(finalValue);
      }
    }, 100);
  };

  const movePlayer = (steps) => {
    const maxRounds = gameMode === 'rounds1' ? 1 : gameMode === 'rounds2' ? 2 : null;
    const currentLaps = lapsCompleted[currentPlayerIndex];

    // Spillere som er i mål for den gjeldende runden skal ikke bevege seg
    if (maxRounds && currentLaps >= maxRounds) {
      setGameMessage(`${players[currentPlayerIndex].name} har fullført spillet og venter på de andre!`);
      nextTurn();
      return;
    }

    setAnimatingPlayer(currentPlayerIndex);
    const newPositions = [...positions];
    const oldPosition = newPositions[currentPlayerIndex];
    let targetPosition = oldPosition + steps;
    
    // Beregn den endelige posisjonen, og sjekk for overskridelse i runder-modus
    const isGoingPastGoal = targetPosition >= BOARD_SIZE;
    let finalPosition;

    if (gameMode !== 'time' && isGoingPastGoal) {
      finalPosition = 0;
    } else {
      finalPosition = targetPosition % BOARD_SIZE;
    }

    let currentStep = 0;
    const moveInterval = setInterval(() => {
      currentStep++;
      const tempPositions = [...newPositions];
      // Avslutt animasjonen nøyaktig på mål hvis man overskrider
      const currentPos = isGoingPastGoal && currentStep >= steps ? finalPosition : (oldPosition + currentStep);
      tempPositions[currentPlayerIndex] = currentPos;
      setPositions(tempPositions);
      
      // Sjekk for fullført runde
      if (currentPos === 0 && oldPosition !== 0) {
        const newLaps = [...lapsCompleted];
        newLaps[currentPlayerIndex]++;
        setLapsCompleted(newLaps);

        // Sjekk for bonuspoeng om man er den første til å fullføre en runde
        if (maxRounds) { // Siden dette kun gjelder for runder-modus
          const playersInGoal = players.filter((_, i) => lapsCompleted[i] >= maxRounds).length;
          if (playersInGoal === 0) {
            const newScores = [...scores];
            newScores[currentPlayerIndex] += 2;
            setScores(newScores);
            setGameMessage(`${players[currentPlayerIndex].name} er først i mål og får 2 bonuspoeng!`);
          }
        }
      }

      // Stopp animasjonen
      if (currentStep >= steps) {
        clearInterval(moveInterval);
        
        const newPositionsFinal = [...positions];
        newPositionsFinal[currentPlayerIndex] = finalPosition;
        setPositions(newPositionsFinal);

        setTimeout(() => {
          setAnimatingPlayer(-1);
          // Hvis spilleren landet på felt 0, skal de ikke svare på spørsmål.
          if (finalPosition === 0) {
            nextTurn();
          } else {
            handleFieldAction(finalPosition);
          }
        }, 500);
      }
    }, 300);
  };

  const showReflectionQuestion = () => {
    const question = REFLECTION_QUESTIONS[Math.floor(Math.random() * REFLECTION_QUESTIONS.length)];
    setCurrentQuestion({
      type: 'reflection',
      question: question
    });
    setSelectedPlayersForReflection(new Set());
    setShowModal(true);
  };

  const togglePlayerReflectionParticipation = (playerIndex) => {
    const newSelected = new Set(selectedPlayersForReflection);
    if (newSelected.has(playerIndex)) {
      newSelected.delete(playerIndex);
    } else {
      newSelected.add(playerIndex);
    }
    setSelectedPlayersForReflection(newSelected);
  };

  const completeReflectionDiscussion = () => {
    const newScores = [...scores];
    selectedPlayersForReflection.forEach(playerIndex => {
      newScores[playerIndex] += 1;
    });
    setScores(newScores);
    
    continueGame();
  };

  const handleFieldAction = (fieldIndex) => {
    const fieldType = getFieldType(fieldIndex);
    
    setWaitingForAnswer(true);
    
    setTimeout(() => {
      if (fieldType === 'fact') {
        showFactQuestion();
      } else if (fieldType === 'reflection') {
        showReflectionQuestion();
      } else if (fieldType === 'challenge') {
        showChallengeCard();
      } else if (fieldType === 'chance') {
        showChanceCard();
      } else {
        nextTurn();
      }
    }, 500);
  };

  const showFactQuestion = () => {
    const question = FACT_QUESTIONS[Math.floor(Math.random() * FACT_QUESTIONS.length)];
    setCurrentQuestion({
      type: 'fact',
      ...question,
      selectedAnswer: null,
      showResult: false
    });
    setShowModal(true);
  };

 const showChallengeCard = () => {
  const challengeCard = CHALLENGE_CARDS[Math.floor(Math.random() * CHALLENGE_CARDS.length)];
  setCurrentQuestion({
    type: 'challenge',
    text: challengeCard.text,
    answer: challengeCard.answer,
    completed: false,
    showAnswer: false // Legger til ny tilstand for å vise svaret
  });
  setShowModal(true);
};

  const showChanceCard = () => {
    const chanceCard = CHANCE_CARDS[Math.floor(Math.random() * CHANCE_CARDS.length)];
    setCurrentQuestion({
      type: 'chance',
      ...chanceCard
    });
    setShowModal(true);
  };

  const answerQuestion = (answerIndex) => {
    if (currentQuestion.showResult) return;
    
    const correct = answerIndex === currentQuestion.correct;
    setCurrentQuestion(prev => ({
      ...prev,
      selectedAnswer: answerIndex,
      showResult: true
    }));
    
    if (correct) {
      const newScores = [...scores];
      newScores[currentPlayerIndex] += 2;
      setScores(newScores);
      setGameMessage(`Riktig! ${players[currentPlayerIndex].name} får 2 poeng!`);
    } else {
      setGameMessage(`Feil svar, men bra forsøk!`);
    }
  };

  const completeChallenge = (success) => {
    setCurrentQuestion(prev => ({
      ...prev,
      completed: true,
      success: success
    }));
    
    if (success) {
      const newScores = [...scores];
      newScores[currentPlayerIndex] += 2;
      setScores(newScores);
    }
  };

  const handleChanceCard = () => {
    const card = currentQuestion;
    
    if (card.points) {
      const newScores = [...scores];
      newScores[currentPlayerIndex] += card.points;
      setScores(newScores);
    }
    
    setShowModal(false);
    
    if (card.extraTurn) {
      setWaitingForAnswer(false);
      setGameMessage(`${players[currentPlayerIndex].name} får en ekstra tur!`);
    } else {
      nextTurn();
    }
  };

  const continueGame = () => {
    setShowModal(false);
    nextTurn();
  };

  const nextTurn = () => {
    if (checkGameEnd()) {
      return;
    }

    setWaitingForAnswer(false);
    const maxRounds = gameMode === 'rounds1' ? 1 : gameMode === 'rounds2' ? 2 : null;
    let newPlayerIndex = (currentPlayerIndex + 1) % players.length;

    // Hopper over spillere som har fullført sine runder
    let attempts = 0;
    while (maxRounds && lapsCompleted[newPlayerIndex] >= maxRounds && attempts < players.length) {
      newPlayerIndex = (newPlayerIndex + 1) % players.length;
      attempts++;
    }

    setCurrentPlayerIndex(newPlayerIndex);
    setCurrentQuestion(null);
    setGameMessage(`${players[newPlayerIndex].name}s tur!`);
  };

  const resetGameState = () => {
    if (timerIntervalId) {
      clearInterval(timerIntervalId);
      setTimerIntervalId(null);
    }
    setGameState('setup');
    setGameWinner(null);
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setPositions([]);
    setScores([]);
    setLapsCompleted([]);
    setDiceValue(1);
    setIsRolling(false);
    setShowModal(false);
    setCurrentQuestion(null);
    setTimeLeft(null);
    setAnimatingPlayer(-1);
    setGameMessage('Velkommen til spillet!');
    setWaitingForAnswer(false);
    setHoveredField(null);
    setSelectedPlayersForReflection(new Set());
  };

  const WinnerScreen = () => {
    if (!gameWinner) return null;

    const { winners, finalScores } = gameWinner;
    const sortedScores = [...finalScores].sort((a, b) => b.score - a.score || b.rounds - a.rounds);
    
    const isTie = winners.length > 1;

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #7c3aed 50%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '48px',
          maxWidth: '800px',
          width: '100%',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <h1 style={{ 
            fontSize: '48px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '32px',
            margin: '0 0 32px 0'
          }}>
            🎉 Spillet er ferdig! 🎉
          </h1>
          
          {isTie ? (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: '#059669',
                margin: '0 0 16px 0'
              }}>
                Uavgjort! {winners.length} spillere vant!
              </h2>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                {winners.map((winner, index) => (
                  <div key={index}>
                    <div 
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        margin: '0 auto 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '28px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        backgroundColor: winner.color
                      }}
                    >
                      {winner.avatar}
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{winner.name}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: '#059669',
                margin: '0 0 16px 0'
              }}>
                Vinner: {winners[0].name}!
              </h2>
              <div 
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '40px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  backgroundColor: winners[0].color
                }}
              >
                {winners[0].avatar}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#374151', 
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>
              Sluttresultat
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sortedScores.map((playerData, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: index === 0 ? '#fef3c7' : '#f9fafb',
                  padding: '16px',
                  borderRadius: '12px',
                  border: index === 0 ? '2px solid #fbbf24' : '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', minWidth: '24px' }}>
                      #{index + 1}
                    </div>
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        backgroundColor: playerData.player.color
                      }}
                    >
                      {playerData.player.avatar}
                    </div>
                    <div style={{ fontWeight: '600', fontSize: '18px' }}>
                      {playerData.player.name}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                      {playerData.score} poeng
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {playerData.rounds} runder fullført
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={resetGameState}
            style={{
              background: 'linear-gradient(90deg, #3b82f6 0%, #7c3aed 100%)',
              color: 'white',
              padding: '16px 32px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '18px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
          >
            Spill igjen
          </button>
        </div>
      </div>
    );
  };

  const PlayerSetup = () => {
    const playerCount = playerConfigs.length;

    const updatePlayerCount = (count) => {
      let newConfigs = Array.from({ length: count }, (_, i) => 
        playerConfigs[i] || { name: '', color: i % PLAYER_COLORS.length, avatar: i % PLAYER_AVATARS.length }
      );
      setPlayerConfigs(newConfigs);
    };

    const updatePlayerConfig = (index, field, value) => {
      const newConfigs = [...playerConfigs];
      newConfigs[index] = { ...newConfigs[index], [field]: value };
      setPlayerConfigs(newConfigs);
    };

    const startGameWithConfig = () => {
      const playerData = playerConfigs.map((config, i) => ({
        name: config.name || `Spiller ${i + 1}`,
        color: PLAYER_COLORS[config.color],
        avatar: PLAYER_AVATARS[config.avatar]
      }));
      
      setPlayers(playerData);
      setPositions(new Array(playerCount).fill(0));
      setScores(new Array(playerCount).fill(0));
      setLapsCompleted(new Array(playerCount).fill(0));
      setCurrentPlayerIndex(0);
      setGameState('playing');
      setGameMessage(`${playerData[0].name} starter!`);
      
      if (gameMode === 'time') {
        const totalSeconds = customTime * 60;
        setTimeLeft(totalSeconds);
        
        const timer = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        setTimerIntervalId(timer);
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #7c3aed 50%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '32px',
          maxWidth: '1024px',
          width: '100%',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{ 
              fontSize: '36px', 
              fontWeight: 'bold', 
              color: '#374151', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>
              Religioner og Livssyn
            </h1>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '18px',
              margin: '0'
            }}>
              Et læringsspill om verdensreligioner og livssyn
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '24px' 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <Users style={{ 
                    display: 'inline', 
                    width: '16px', 
                    height: '16px', 
                    marginRight: '8px' 
                  }} />
                  Antall spillere:
                </label>
                <select 
                  value={playerConfigs.length} 
                  onChange={(e) => updatePlayerCount(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  {[2,3,4,5,6].map(n => (
                    <option key={n} value={n}>{n} spillere</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <Clock style={{ 
                    display: 'inline', 
                    width: '16px', 
                    height: '16px', 
                    marginRight: '8px' 
                  }} />
                  Spillmodus:
                </label>
                <select 
                  value={gameMode}
                  onChange={(e) => setGameMode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="rounds1">1 runde</option>
                  <option value="rounds2">2 runder</option>
                  <option value="time">Tid</option>
                </select>
                
                {gameMode === 'time' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '12px', 
                      fontWeight: '500', 
                      color: '#6b7280', 
                      marginBottom: '4px' 
                    }}>
                      Antall minutter:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={customTime}
                      onChange={(e) => setCustomTime(parseInt(e.target.value) || 15)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                Spillere:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {playerConfigs.map((config, i) => (
                  <div key={i} style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                      gap: '12px', 
                      alignItems: 'center' 
                    }}>
                      <input
                        type="text"
                        placeholder={`Spiller ${i + 1}`}
                        value={config.name}
                        onChange={(e) => updatePlayerConfig(i, 'name', e.target.value)}
                        style={{
                          padding: '8px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      
                      <select
                        value={config.color}
                        onChange={(e) => updatePlayerConfig(i, 'color', Number(e.target.value))}
                        style={{
                          padding: '8px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '12px',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        {['Rød', 'Blå', 'Grønn', 'Orange', 'Lilla', 'Turkis'].map((colorName, index) => (
                          <option key={index} value={index}>{colorName}</option>
                        ))}
                      </select>
                      
                      <select
                        value={config.avatar}
                        onChange={(e) => updatePlayerConfig(i, 'avatar', Number(e.target.value))}
                        style={{
                          padding: '8px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '12px',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        {AVATAR_NAMES.map((avatarName, index) => (
                          <option key={index} value={index}>
                            {PLAYER_AVATARS[index]} {avatarName}
                          </option>
                        ))}
                      </select>
                      
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            backgroundColor: PLAYER_COLORS[config.color]
                          }}
                        >
                          {PLAYER_AVATARS[config.avatar]}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={startGameWithConfig}
              style={{
                width: '100%',
                background: 'linear-gradient(90deg, #3b82f6 0%, #7c3aed 100%)',
                color: 'white',
                padding: '16px',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '18px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              <Play style={{ width: '20px', height: '20px' }} />
              Start spill
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DiceFace = ({ value }) => {
    // Pikselbaserte posisjoner for en 96px x 96px terning
    const getDots = (value) => {
        const positions = [];
        const dotSize = 16; // Samme som width/height på prikkene

        switch (value) {
            case 1:
                positions.push({ top: 58 - dotSize / 2, left: 56 - dotSize / 2 }); // Sentrum (symmetrisk midtpunkt)
                break;
            case 2:
                positions.push({ top: 34 - dotSize / 2, left: 34 - dotSize / 2 }); // Øvre midten
                positions.push({ top: 82 - dotSize / 2, left: 78 - dotSize / 2 }); // Nedre midten
                break;
            case 3:
                positions.push({ top: 34 - dotSize / 2, left: 34 - dotSize / 2 }); // Øvre midten
                positions.push({ top: 58 - dotSize / 2, left: 56 - dotSize / 2 }); // Sentrum
                positions.push({ top: 82 - dotSize / 2, left: 78 - dotSize / 2 }); // Nedre midten
                break;
            case 4:
                positions.push({ top: 34 - dotSize / 2, left: 34 - dotSize / 2 }); // Øvre venstre
                positions.push({ top: 34 - dotSize / 2, left: 78 - dotSize / 2 }); // Øvre høyre
                positions.push({ top: 82 - dotSize / 2, left: 34 - dotSize / 2 }); // Nedre venstre
                positions.push({ top: 82 - dotSize / 2, left: 78 - dotSize / 2 }); // Nedre høyre
                break;
            case 5:
                positions.push({ top: 34 - dotSize / 2, left: 34 - dotSize / 2 }); // Øvre venstre
                positions.push({ top: 34 - dotSize / 2, left: 78 - dotSize / 2 }); // Øvre høyre
                positions.push({ top: 58 - dotSize / 2, left: 56 - dotSize / 2 }); // Sentrum
                positions.push({ top: 82 - dotSize / 2, left: 34 - dotSize / 2 }); // Nedre venstre
                positions.push({ top: 82 - dotSize / 2, left: 78 - dotSize / 2 }); // Nedre høyre
                break;
            case 6:
                positions.push({ top: 34 - dotSize / 2, left: 34 - dotSize / 2 }); // Øvre venstre
                positions.push({ top: 34 - dotSize / 2, left: 78 - dotSize / 2 }); // Øvre høyre
                positions.push({ top: 58 - dotSize / 2, left: 34 - dotSize / 2 }); // Midten venstre
                positions.push({ top: 58 - dotSize / 2, left: 78 - dotSize / 2 }); // Midten høyre
                positions.push({ top: 82 - dotSize / 2, left: 34 - dotSize / 2 }); // Nedre venstre
                positions.push({ top: 82 - dotSize / 2, left: 78 - dotSize / 2 }); // Nedre høyre
                break;
            default:
                positions.push({ top: 38 - dotSize / 2, left: 56 - dotSize / 2 }); // Fallback til sentrum
                break;
        }
        
        return positions;
    };

    const dots = getDots(value);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                padding: 0,
                margin: 0
            }}
        >
            {dots.map((position, index) => (
                <div
                    key={index}
                    style={{
                        position: 'absolute',
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#374151',
                        borderRadius: '50%',
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        transform: 'translate(-50%, -50%)'
                    }}
                />
            ))}
        </div>
    );
  };

  const GameBoard = () => {
    const cellSize = 120;
    const startFieldInfo = getFieldInfo('start');

    return (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
        
        <div style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #1e293b 100%)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '2px solid #475569',
          marginBottom: '24px',
          width: '95vw',
          height: '75vh',
          minHeight: '700px'
        }}>
          
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(71, 85, 105, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
              backdropFilter: 'blur(8px)',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              textAlign: 'center'
            }}>
              
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <div 
                  style={{
                    width: '96px',
                    height: '96px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 50%, #e5e7eb 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #d1d5db',
                    cursor: waitingForAnswer ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    transform: isRolling ? 'rotate(360deg) scale(1.1)' : 'scale(1)',
                    animation: isRolling ? 'spin 0.1s linear infinite' : 'none'
                  }}
                  onClick={rollDice}
                  onMouseEnter={(e) => {
                    if (!isRolling && !waitingForAnswer) {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isRolling && !waitingForAnswer) {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                >
                  <DiceFace value={diceValue} />
                </div>
              </div>
              
              <div style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                {players[currentPlayerIndex]?.name}s tur
              </div>
              
              <div style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px'
              }}>
                {waitingForAnswer ? 'Svar på spørsmålet!' : 'Klikk terningen for å kaste'}
              </div>
              
            </div>
          </div>
          
          {Array.from({length: BOARD_SIZE}, (_, i) => {
            const pos = getBoardPosition(i);
            const fieldType = getFieldType(i);
            const fieldInfo = getFieldInfo(fieldType);
            const isHovered = hoveredField === i;
            const hasPlayerOnField = positions.includes(i);
            
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  left: `calc(50% + ${pos.x}px - ${cellSize / 2}px)`,
                  top: `calc(50% + ${pos.y}px - ${cellSize / 2}px)`,
                  background: i === 0 ? `linear-gradient(135deg, ${getFieldInfo('start').color}ee, ${getFieldInfo('start').color}aa)` : `linear-gradient(135deg, ${fieldInfo.color}ee, ${fieldInfo.color}aa)`,
                  borderRadius: '16px',
                  boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                  zIndex: isHovered ? 20 : (hasPlayerOnField ? 15 : 5),
                  outline: hasPlayerOnField ? '4px solid #fbbf24' : 'none',
                  outlineOffset: hasPlayerOnField ? '4px' : '0'
                }}
                onMouseEnter={() => setHoveredField(i)}
                onMouseLeave={() => setHoveredField(null)}
              >
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{i === 0 ? startFieldInfo.icon : fieldInfo.icon}</div>
                  {i === 0 && <div style={{ fontSize: '16px', fontWeight: 'bold' }}>START/MÅL</div>}
                  {isHovered && i > 0 && (
                    <div style={{ fontSize: '12px', lineHeight: '1.2', textAlign: 'center', padding: '0 8px' }}>
                      {fieldInfo.name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {players.length > 0 && positions.map((position, playerIndex) => {
            const pos = getBoardPosition(position % BOARD_SIZE);
            const playersOnSameField = positions.filter(p => p % BOARD_SIZE === position % BOARD_SIZE);
            const totalOnField = playersOnSameField.length;
            
            let offsetX = 0;
            let offsetY = 0;
            if (totalOnField > 1) {
              const playerOrderOnField = positions.filter((p, i) => p % BOARD_SIZE === position % BOARD_SIZE && i < playerIndex).length;
              const angle = (playerOrderOnField / totalOnField) * 2 * Math.PI;
              offsetX = Math.cos(angle) * 25;
              offsetY = Math.sin(angle) * 25;
            }
            
            const isCurrentPlayer = playerIndex === currentPlayerIndex;
            const isAnimating = animatingPlayer === playerIndex;
            const size = isCurrentPlayer ? 56 : 48;
            
            return (
              <div
                key={playerIndex}
                style={{
                  position: 'absolute',
                  width: `${size}px`,
                  height: `${size}px`,
                  backgroundColor: players[playerIndex].color,
                  left: `calc(50% + ${pos.x + offsetX}px - ${size / 2}px)`,
                  top: `calc(50% + ${pos.y + offsetY}px - ${size / 2}px)`,
                  borderRadius: '50%',
                  border: `4px solid ${isCurrentPlayer ? '#fbbf24' : 'white'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isCurrentPlayer ? 
                    `0 0 20px ${players[playerIndex].color}aa, 0 6px 15px rgba(0,0,0,0.4)` : 
                    '0 3px 10px rgba(0,0,0,0.3)',
                  transition: 'all 0.5s',
                  transform: isAnimating ? 'scale(1.25)' : (isCurrentPlayer ? 'scale(1.1)' : 'scale(1)'),
                  zIndex: isAnimating ? 40 : (isCurrentPlayer ? 30 : 25)
                }}
              >
                <div style={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: isCurrentPlayer ? '24px' : '20px'
                }}>
                  {players[playerIndex].avatar}
                </div>
                
                {isCurrentPlayer && (
                  <div style={{
                    position: 'absolute',
                    inset: '0',
                    borderRadius: '50%',
                    border: '4px solid #fde047',
                    animation: 'pulse 2s ease-in-out infinite',
                    opacity: '0.4'
                  }}></div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Dette er den flyttede spiller-informasjons-blokken */}
       <div style={{
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderRadius: '16px',
    boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    width: '95vw',
    marginBottom: '16px'
  }}>
    <div style={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      justifyContent: 'center', 
      gap: '12px' 
    }}>
      {players.map((player, index) => (
        <div key={index} style={{
          textAlign: 'center',
          padding: '12px',
          borderRadius: '8px',
          transition: 'all 0.3s',
          minWidth: '120px',
          backgroundColor: index === currentPlayerIndex ? '#fef3c7' : '#f9fafb',
          border: index === currentPlayerIndex ? '2px solid #fbbf24' : '1px solid #e5e7eb',
          transform: index === currentPlayerIndex ? 'scale(1.05)' : 'scale(1)',
          boxShadow: index === currentPlayerIndex ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
        }}>
          <div 
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              margin: '0 auto 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              backgroundColor: player.color
            }}
          >
            {player.avatar}
          </div>
          <div style={{ 
            fontWeight: '600', 
            color: '#374151', 
            fontSize: '14px', 
            marginBottom: '4px' 
          }}>
            {player.name}
          </div>
          {/* Poengsummen er nå flyttet opp */}
          <div style={{ 
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '4px' // Legger til marg under poengsum
          }}>
            {scores[index]}p
          </div>
          {/* Rundestatus er flyttet ned */}
          <div style={{ 
            fontSize: '11px', 
            color: '#6b7280'
          }}>
            {gameMode === 'rounds1' ? `Runde ${lapsCompleted[index] + 1}/1` : 
              gameMode === 'rounds2' ? `Runde ${lapsCompleted[index] + 1}/2` : 
              `Runde ${lapsCompleted[index] + 1}`}
          </div>
        </div>
      ))}
    </div>
    
    <div style={{ textAlign: 'center', marginTop: '16px' }}>
      <div style={{ 
        fontSize: '16px', 
        color: '#374151', 
        fontWeight: '500' 
      }}>
        {gameMessage}
      </div>
      {timeLeft !== null && (
        <div style={{ 
          fontSize: '18px', 
          fontWeight: 'bold', 
          color: '#3b82f6', 
          marginTop: '8px' 
        }}>
          Tid: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </div>
      )}
    </div>
  </div>
        
        {/* Dette er felt-forklaringsblokken, nå flyttet under spillerinfo */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          marginBottom: '16px',
          width: '95vw'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: 'bold', 
              color: '#374151', 
              marginBottom: '12px',
              margin: '0 0 12px 0'
            }}>
              Felttyper
            </h3>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: '16px' 
            }}>
              {[
                { label: '📚 Faktaspørsmål' },
                { label: '💭 Refleksjon' },
                { label: '⚡ Utfordring' },
                { label: '🎲 Sjanse' },
                { label: '🏁 Start/Mål' }
              ].map((field, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>{field.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const QuestionModal = () => {
    if (!showModal || !currentQuestion) return null;
    
    // Funksjon for å vise svaret på utfordringskort
    const handleShowAnswer = () => {
      setCurrentQuestion(prev => ({
        ...prev,
        showAnswer: true
      }));
    };

    return (
      <div style={{
        position: 'fixed',
        inset: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 50
      }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '1024px',
          width: '100%',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#374151',
              margin: '0'
            }}>
              {currentQuestion.type === 'fact' && `Faktaspørsmål - ${currentQuestion.religion}`}
              {currentQuestion.type === 'reflection' && 'Refleksjonsspørsmål'}
              {currentQuestion.type === 'challenge' && 'Utfordring'}
              {currentQuestion.type === 'chance' && 'Sjansekort'}
            </h3>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <p style={{ 
              fontSize: '18px', 
              color: '#374151',
              margin: '0'
            }}>
              {currentQuestion.question || currentQuestion.text}
            </p>
          </div>
          
          {currentQuestion.type === 'fact' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {currentQuestion.options.map((option, index) => {
                let buttonStyle = {
                  width: '100%',
                  padding: '16px',
                  textAlign: 'left',
                  borderRadius: '12px',
                  border: '2px solid',
                  transition: 'all 0.2s',
                  fontWeight: '500',
                  fontSize: '16px',
                  cursor: currentQuestion.showResult ? 'default' : 'pointer',
                  backgroundColor: '#ffffff'
                };
                
                if (currentQuestion.showResult) {
                  if (index === currentQuestion.correct) {
                    buttonStyle.borderColor = '#10b981';
                    buttonStyle.backgroundColor = '#d1fae5';
                    buttonStyle.color = '#065f46';
                  } else if (index === currentQuestion.selectedAnswer) {
                    buttonStyle.borderColor = '#ef4444';
                    buttonStyle.backgroundColor = '#fee2e2';
                    buttonStyle.color = '#991b1b';
                  } else {
                    buttonStyle.borderColor = '#e5e7eb';
                    buttonStyle.backgroundColor = '#f9fafb';
                    buttonStyle.color = '#6b7280';
                  }
                } else {
                  buttonStyle.borderColor = '#e5e7eb';
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => answerQuestion(index)}
                    disabled={currentQuestion.showResult}
                    style={buttonStyle}
                    onMouseEnter={(e) => {
                      if (!currentQuestion.showResult) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.backgroundColor = '#dbeafe';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!currentQuestion.showResult) {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    {option}
                  </button>
                );
              })}
              
              {currentQuestion.showResult && (
                <div style={{ marginTop: '24px', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                  {currentQuestion.selectedAnswer === currentQuestion.correct ? (
                    <p style={{ color: '#10b981', fontWeight: '600', fontSize: '18px', margin: '0 0 16px 0' }}>
                      Riktig! +2 poeng
                    </p>
                  ) : (
                    <p style={{ color: '#ef4444', fontWeight: '600', fontSize: '18px', margin: '0 0 16px 0' }}>
                      Feil svar. Riktig svar er markert i grønt.
                    </p>
                  )}
                  <button
                    onClick={continueGame}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '16px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                  >
                    Fortsett spill
                  </button>
                </div>
              )}
            </div>
          )}
          
          {currentQuestion.type === 'reflection' && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{
                backgroundColor: '#dbeafe',
                border: '2px solid #3b82f6',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <p style={{ color: '#1e40af', fontWeight: '500', marginBottom: '16px', fontSize: '16px' }}>
                  Velg spillere som deltok aktivt i diskusjonen:
                </p>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '12px' 
                }}>
                  {players.map((player, index) => (
                    <button
                      key={index}
                      onClick={() => togglePlayerReflectionParticipation(index)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid',
                        borderColor: selectedPlayersForReflection.has(index) ? '#3b82f6' : '#d1d5db',
                        backgroundColor: selectedPlayersForReflection.has(index) ? '#dbeafe' : 'white',
                        color: selectedPlayersForReflection.has(index) ? '#1e40af' : '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedPlayersForReflection.has(index)) {
                          e.target.style.borderColor = '#93c5fd';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedPlayersForReflection.has(index)) {
                          e.target.style.borderColor = '#d1d5db';
                        }
                      }}
                    >
                      <div 
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          margin: '0 auto 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          backgroundColor: player.color
                        }}
                      >
                        {player.avatar}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{player.name}</div>
                      {selectedPlayersForReflection.has(index) && (
                        <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>+1 poeng</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={{
                backgroundColor: '#d1fae5',
                border: '2px solid #10b981',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <p style={{ color: '#065f46', marginBottom: '16px', fontSize: '16px' }}>
                  {players[currentPlayerIndex].name}, start diskusjonen om:
                </p>
                <p style={{ color: '#047857', fontWeight: '500', fontSize: '20px', marginBottom: '16px' }}>
                  "{currentQuestion.question}"
                </p>
                <p style={{ color: '#10b981', fontSize: '14px', margin: '0' }}>
                  Ta deg tid til å dele tanker og lytte til hverandre.
                </p>
              </div>
              
              <button
                onClick={completeReflectionDiscussion}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                Fullfør diskusjon {selectedPlayersForReflection.size === 0 ? '(ingen får poeng)' : ''}
              </button>
            </div>
          )}
          
          {currentQuestion.type === 'challenge' && !currentQuestion.completed && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{
                backgroundColor: '#fed7aa',
                border: '2px solid #ea580c',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <p style={{ color: '#9a3412', fontWeight: '500', fontSize: '18px', margin: '0 0 16px 0' }}>
                  {currentQuestion.text}
                </p>
                {!currentQuestion.showAnswer && (
                  <button
                    onClick={handleShowAnswer}
                    style={{
                      padding: '8px 24px',
                      backgroundColor: '#f39c12',
                      color: 'white',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      marginTop: '16px'
                    }}
                  >
                    Vis svar
                  </button>
                )}
                {currentQuestion.showAnswer && (
                  <p style={{ color: '#ea580c', fontSize: '16px', margin: '16px 0 0 0' }}>
                    **Svar**: {currentQuestion.answer}
                  </p>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button
                  onClick={() => completeChallenge(true)}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  Utfordring løst! (+2 poeng)
                </button>
                <button
                  onClick={() => completeChallenge(false)}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                >
                  Ikke løst riktig (0 poeng)
                </button>
              </div>
            </div>
          )}
          
          {currentQuestion.type === 'challenge' && currentQuestion.completed && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: currentQuestion.success ? '#d1fae5' : '#f3f4f6',
                border: currentQuestion.success ? '2px solid #10b981' : '2px solid #9ca3af'
              }}>
                <p style={{ 
                  fontWeight: '600', 
                  fontSize: '18px',
                  margin: '0',
                  color: currentQuestion.success ? '#065f46' : '#374151'
                }}>
                  {currentQuestion.success ? 'Flott! +2 poeng' : 'Bra forsøk! Prøv igjen neste gang.'}
                </p>
              </div>
              
              <button
                onClick={continueGame}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                Fortsett spill
              </button>
            </div>
          )}
          
          {currentQuestion.type === 'chance' && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{
                backgroundColor: '#fef3c7',
                border: '2px solid #eab308',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <p style={{ color: '#92400e', fontSize: '18px', fontWeight: '600', margin: '0' }}>
                  {currentQuestion.text}
                </p>
                
                {currentQuestion.points !== 0 && (
                  <div style={{
                    marginTop: '16px',
                    fontSize: '18px',
                    borderRadius: '8px',
                    padding: '12px',
                    fontWeight: 'bold',
                    backgroundColor: currentQuestion.points > 0 ? '#dcfce7' : '#fee2e2',
                    color: currentQuestion.points > 0 ? '#166534' : '#991b1b'
                  }}>
                    {currentQuestion.points > 0 ? '+' : ''}{currentQuestion.points} poeng
                  </div>
                )}
                
                {currentQuestion.extraTurn && (
                  <div style={{
                    marginTop: '16px',
                    fontSize: '18px',
                    borderRadius: '8px',
                    padding: '12px',
                    fontWeight: 'bold',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af'
                  }}>
                    Du får en ekstra tur!
                  </div>
                )}
              </div>
              
              <button
                onClick={handleChanceCard}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#eab308',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#ca8a04'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#eab308'}
              >
                Fortsett
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Denne delen av koden er utenfor QuestionModal, som den skal være.
  if (gameState === 'setup') {
    return <PlayerSetup />;
  }

  if (gameState === 'finished') {
    return <WinnerScreen />;
  }

  // Den endelige return-setningen for hovedkomponenten.
  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #7c3aed 50%, #1e293b 100%)'
      }}>
        <div style={{ padding: '16px' }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: '16px',
            boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#374151',
                margin: '0'
              }}>
                Religioner og Livssyn - Læringsspill
              </h1>
              <button
                onClick={resetGameState}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
              >
                <RotateCcw style={{ width: '16px', height: '16px' }} />
                Nytt spill
              </button>
            </div>
          </div>
          
          <GameBoard />
        </div>
      </div>
      
      <QuestionModal />
      
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.8;
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default ReligionGame;